import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ApiError, api } from "@/lib/api";
import {
  AUTH_CHANGED_EVENT,
  getFamilyToken,
  setFamilyToken,
} from "@/lib/auth";

export function AuthGate({ children }: { children: ReactNode }) {
  const [token, setToken] = useState(getFamilyToken);
  const [checking, setChecking] = useState(!!token);

  useEffect(() => {
    const changed = () => setToken(getFamilyToken());
    window.addEventListener(AUTH_CHANGED_EVENT, changed);
    return () => window.removeEventListener(AUTH_CHANGED_EVENT, changed);
  }, []);

  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    let cancelled = false;
    setChecking(true);
    api.auth.me()
      .catch((error) => {
        // A network failure is expected when opening the installed PWA
        // offline. Keep the signed local session; only an authoritative 401
        // means the token should be discarded.
        if (error instanceof ApiError && error.status === 401) {
          setFamilyToken(null);
        }
      })
      .finally(() => {
        if (!cancelled) setChecking(false);
      });
    return () => { cancelled = true; };
  }, [token]);

  if (checking) return <AuthLoading />;
  if (!token) return <SignIn />;
  return <>{children}</>;
}

function SignIn() {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!code.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      const session = await api.auth.login(code.trim());
      setFamilyToken(session.token);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "No se pudo entrar");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="min-h-[100dvh] safe-pt safe-pb px-5 grid place-items-center">
      <motion.form
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        onSubmit={submit}
        className="chunk p-6 w-full max-w-sm text-center"
      >
        <div className="text-6xl" aria-hidden>🤝</div>
        <h1 className="mt-2 font-display text-3xl">DuoMatt</h1>
        <p className="mt-2 text-sm text-brand-muted font-bold">
          Escribe el código familiar una vez en este dispositivo.
        </p>
        <label className="block mt-6 text-left text-xs uppercase tracking-widest text-brand-muted font-black">
          Código familiar
          <input
            autoFocus
            type="password"
            autoComplete="current-password"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            className="mt-2 w-full rounded-2xl border-2 border-brand-border bg-brand-surface px-4 py-4 text-center text-xl font-display focus:border-brand-violet focus:outline-none"
          />
        </label>
        {error && <p className="mt-3 text-sm font-bold text-brand-rose">{error}</p>}
        <button
          type="submit"
          disabled={busy || !code.trim()}
          className="chunk-btn-primary mt-4 w-full disabled:opacity-50"
        >
          {busy ? "Entrando…" : "Entrar en la familia"}
        </button>
      </motion.form>
    </main>
  );
}

function AuthLoading() {
  return (
    <div className="min-h-[100dvh] grid place-items-center text-center">
      <div>
        <div className="text-5xl animate-pulse">🤝</div>
        <div className="mt-3 font-display text-xl">Abriendo DuoMatt…</div>
      </div>
    </div>
  );
}
